import type { ReactNode } from 'react';
import {
    Badge,
    BlockStack,
    Box,
    Card,
    DataTable,
    InlineStack,
    SegmentedControl,
    Typography,
    type BadgeTone,
    type DataTableColumn,
    type SegmentedOption,
} from '@pv-frontend/ui-components-web';
import {
    complimentaryStanding,
    type ComplimentaryStanding,
    type ComplimentaryView,
} from './complimentary-standing';
import { ComplimentaryProPanel, type GrantProAction, type RevokeProAction } from './complimentary-pro-panel';
import { formatDate } from './format-date';

/**
 * Plain view models for the vault detail screen — deliberately decoupled from
 * the Relay generated types so this presentational component is unit-testable
 * without a Relay harness or the Babel transform. The screen maps `vaultDetail`
 * query data into these.
 */
export interface VaultMemberView {
    accountId: string;
    email: string | null;
    roleName: string;
    joinedUtc: string;
    lastLoginUtc: string | null;
}

/** A per-vault flag override currently pinned on the vault (`ON` / `OFF`). */
export interface VaultOverrideView {
    flagKey: string;
    state: string;
}

export interface VaultDetailView {
    vaultId: string;
    name: string;
    identifier: string;
    jurisdiction: string;
    ring: string;
    createdUtc: string;
    /** ⚠️ A lapsed or revoked grant still reads Pro · Active here — never infer a comp from these. */
    planName: string | null;
    subscriptionStatus: string | null;
    /** `FREE` / `PAYING` / `COMPLIMENTARY` from the server — the one field that answers paying-vs-free. */
    planBucket: string;
    /** Whether the server's `grantPro` would accept this vault — the only thing that offers Grant. */
    canGrant: boolean;
    /** Complimentary Pro standing and grant history. Operator-only: names staff and their reasons. */
    complimentary: ComplimentaryView;
    members: readonly VaultMemberView[];
    overrides: readonly VaultOverrideView[];
}

/** The two in-context write actions the detail view drives, wired by the screen. */
export interface VaultToggleActions {
    onSetRing: (ring: string) => void;
    onSetOverride: (flagKey: string, state: string) => void;
    onClearOverride: (flagKey: string) => void;
}

export interface VaultDetailProps extends Partial<VaultToggleActions> {
    vault: VaultDetailView;
    /** Every known flag key, so an override control is offered for each. */
    featureKeys: readonly string[];
    /**
     * Toggle keys with a mutation in flight — `'ring'` for the ring control and
     * `override:<flagKey>` for a given override — so just that control disables.
     */
    busyKeys?: ReadonlySet<string>;
    /** Commit a Complimentary Pro grant; resolves null on success or readable copy on refusal. */
    onGrantPro?: GrantProAction;
    /** Commit a Complimentary Pro revoke; resolves null on success or readable copy on refusal. */
    onRevokePro?: RevokeProAction;
}

const RING_OPTIONS: readonly SegmentedOption<string>[] = [
    { value: 'INTERNAL', label: 'Internal' },
    { value: 'BETA', label: 'Beta' },
    { value: 'GA', label: 'GA' },
];

const RING_TONES: Record<string, BadgeTone> = {
    INTERNAL: 'primary',
    BETA: 'warning',
    GA: 'success',
};

const RING_LABELS: Record<string, string> = {
    INTERNAL: 'Internal',
    BETA: 'Beta',
    GA: 'GA',
};

/** Sentinel for the "no override pinned" segment — falls back to the rollout. */
const DEFAULT_OVERRIDE = 'DEFAULT';

const OVERRIDE_OPTIONS: readonly SegmentedOption<string>[] = [
    { value: DEFAULT_OVERRIDE, label: 'Default' },
    { value: 'ON', label: 'On' },
    { value: 'OFF', label: 'Off' },
];

/** Last-login is optional — a vault member may never have signed in. */
function formatLastLogin(iso: string | null): string {
    return iso ? formatDate(iso) : 'Never';
}

const memberColumns: DataTableColumn<VaultMemberView>[] = [
    {
        key: 'member',
        header: 'Member',
        render: (member) =>
            member.email ? (
                <Typography variant="body-sm">{member.email}</Typography>
            ) : (
                <Typography variant="body-sm" tone="text-secondary">
                    —
                </Typography>
            ),
    },
    {
        key: 'role',
        header: 'Role',
        render: (member) => <Badge tone="neutral">{member.roleName}</Badge>,
    },
    {
        key: 'joined',
        header: 'Joined',
        render: (member) => <Typography variant="body-sm">{formatDate(member.joinedUtc)}</Typography>,
    },
    {
        key: 'lastLogin',
        header: 'Last login',
        align: 'end',
        render: (member) => (
            <Typography variant="body-sm" tone={member.lastLoginUtc ? undefined : 'text-secondary'}>
                {formatLastLogin(member.lastLoginUtc)}
            </Typography>
        ),
    },
];

/** A labelled `term : value` pair for the metadata grid. */
function MetaItem({ label, children }: { label: string; children: ReactNode }) {
    return (
        <BlockStack gap="100">
            <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                {label}
            </Typography>
            {children}
        </BlockStack>
    );
}

const noop = () => undefined;

/** The plan badge's tone: a live comp and a paying vault read as active; everything else is neutral. */
function planTone(standing: ComplimentaryStanding): BadgeTone {
    return standing.kind === 'complimentary' || standing.kind === 'paying' ? 'success' : 'neutral';
}

/**
 * Vault detail with in-context write controls: Complimentary Pro grant/revoke
 * (each behind a confirm step), the rollout ring and per-flag overrides.
 * Everything else — members, created date — is presented, never edited.
 * Overrides are offered for every known flag; a flag left at "Default" carries
 * no override and follows the vault's ring.
 */
export function VaultDetail({
    vault,
    featureKeys,
    busyKeys,
    onSetRing = noop,
    onSetOverride = noop,
    onClearOverride = noop,
    onGrantPro,
    onRevokePro,
}: VaultDetailProps) {
    const standing = complimentaryStanding(vault);
    const overrideState = new Map(vault.overrides.map((o) => [o.flagKey, o.state]));
    const ringBusy = busyKeys?.has('ring') ?? false;

    const handleOverride = (flagKey: string, value: string) => {
        if (value === DEFAULT_OVERRIDE) onClearOverride(flagKey);
        else onSetOverride(flagKey, value);
    };

    return (
        <BlockStack gap="500">
            <BlockStack gap="100">
                <Typography variant="heading-lg" as="h1">
                    {vault.name}
                </Typography>
                <Typography variant="body-md" tone="text-secondary">
                    {vault.identifier}
                </Typography>
            </BlockStack>

            <Card>
                <Box padding="400">
                    <InlineStack gap="600" blockAlign="start" wrap>
                        <MetaItem label="Plan">
                            <Badge tone={planTone(standing)} testID="detail-plan">
                                {standing.planLabel}
                            </Badge>
                        </MetaItem>
                        <MetaItem label="Ring">
                            <Badge tone={RING_TONES[vault.ring] ?? 'neutral'} dot testID="detail-ring">
                                {RING_LABELS[vault.ring] ?? vault.ring}
                            </Badge>
                        </MetaItem>
                        <MetaItem label="Jurisdiction">
                            <Typography variant="body-md">{vault.jurisdiction}</Typography>
                        </MetaItem>
                        <MetaItem label="Created">
                            <Typography variant="body-md">{formatDate(vault.createdUtc)}</Typography>
                        </MetaItem>
                    </InlineStack>
                </Box>
            </Card>

            <ComplimentaryProPanel
                vaultName={vault.name}
                vaultIdentifier={vault.identifier}
                standing={standing}
                onGrantPro={onGrantPro}
                onRevokePro={onRevokePro}
            />

            <BlockStack gap="300">
                <Typography variant="heading-sm" as="h2">
                    Rollout ring
                </Typography>
                <Typography variant="body-sm" tone="text-secondary">
                    Which rollout ring this vault sits in. Overrides below take precedence per flag.
                </Typography>
                <SegmentedControl
                    accessibilityLabel="Rollout ring"
                    options={RING_OPTIONS}
                    value={vault.ring}
                    disabled={ringBusy}
                    onChange={(ring) => onSetRing(ring)}
                />
            </BlockStack>

            <BlockStack gap="300">
                <Typography variant="heading-sm" as="h2">
                    Feature overrides
                </Typography>
                <Typography variant="body-sm" tone="text-secondary">
                    Pin a flag on or off for this vault, ahead of its rollout. Default follows the ring.
                </Typography>
                <Card padding="0">
                    {featureKeys.map((flagKey, index) => {
                        const value = overrideState.get(flagKey) ?? DEFAULT_OVERRIDE;
                        const busy = busyKeys?.has(`override:${flagKey}`) ?? false;
                        return (
                            <Box
                                key={flagKey}
                                paddingInline="400"
                                paddingBlock="300"
                                borderColor="border-card"
                                borderBlockStartWidth={index === 0 ? undefined : '0165'}
                            >
                                <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
                                    <Typography variant="body-md" fontWeight="medium">
                                        {flagKey}
                                    </Typography>
                                    <SegmentedControl
                                        accessibilityLabel={`Override for ${flagKey}`}
                                        options={OVERRIDE_OPTIONS}
                                        value={value}
                                        disabled={busy}
                                        onChange={(next) => handleOverride(flagKey, next)}
                                    />
                                </InlineStack>
                            </Box>
                        );
                    })}
                </Card>
            </BlockStack>

            <BlockStack gap="300">
                <Typography variant="heading-sm" as="h2">
                    Members
                </Typography>
                <DataTable
                    columns={memberColumns}
                    rows={vault.members}
                    keyExtractor={(member) => member.accountId}
                    emptyState="This vault has no members."
                    testID="vault-members"
                />
            </BlockStack>
        </BlockStack>
    );
}
